import { useEffect, useRef } from 'react';
import './AppTextField.css';

/**
 * Единственное поле ввода приложения — порт `AppTextField` Android-версии.
 *
 * Что оно даёт даром:
 *  - **Клавиатура не сужается.** `inputMode` здесь всегда `text` и параметром не
 *    выносится: юрист пишет фамилию по-русски, номер дела латиницей и «доб. 214»
 *    в поле телефона. Правило CLAUDE.md §2, проверяется `check-rules.mjs` (rule5).
 *  - **«Готово» убирает клавиатуру.** На iOS её иначе не убрать вовсе: тапом мимо
 *    поля она не закрывается. `enterKeyHint="done"`, Enter снимает фокус.
 *  - **16px в поле.** На меньшем размере iOS увеличивает страницу при фокусе и обратно
 *    сам не возвращает. Правило rule7.
 *
 * Enter не переносит строку и в многострочных полях — так же, как на Android
 * ([D-33](docs/DECISIONS.md)): за поднятой клавиатурой прячется вся форма, и убрать её
 * нужно всегда, а абзац в заметке — иногда.
 */
export function AppTextField({
  value,
  onChange,
  label,
  placeholder,
  multiline = false,
  search = false,
  autoFocus = false,
  selectAllOnFocus = false,
  onDone,
  trailing,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  /** Поле поиска: отличается только тем, как называет себя доступности. */
  search?: boolean;
  autoFocus?: boolean;
  /** Имя из файловой системы («scan_0043») меняют целиком, а не правят по букве. */
  selectAllOnFocus?: boolean;
  onDone?: () => void;
  trailing?: { icon: React.ReactNode; label: string; onClick: () => void };
  id?: string;
}) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const node = ref.current;
    if (!node) return;

    node.focus();
    if (selectAllOnFocus) node.select();
  }, [autoFocus, selectAllOnFocus]);

  const done = () => {
    ref.current?.blur();
    onDone?.();
  };

  const props = {
    ref,
    id,
    className: 'field__control',
    value,
    // Пустой placeholder нужен всегда: по нему CSS понимает, поднимать ли метку.
    placeholder: placeholder ?? ' ',
    inputMode: 'text' as const,
    enterKeyHint: 'done' as const,
    autoCapitalize: 'sentences',
    autoCorrect: 'off',
    spellCheck: false,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(event.target.value),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        done();
      }
    },
  };

  return (
    <label className={`field ${multiline ? 'field--multiline' : ''}`}>
      {multiline ? (
        <textarea {...props} rows={4} />
      ) : (
        /*
         * `type="search"` у поиска — это про доступность, а не про клавиатуру:
         * скринридер называет поле поиском, а раскладка остаётся общей, как требует
         * правило 2 [CLAUDE.md] — узкой клавиатуры этот тип не включает.
         */
        <input {...props} type={search ? 'search' : 'text'} />
      )}
      {label ? <span className="field__label">{label}</span> : null}
      {trailing ? (
        <button
          type="button"
          className="field__trailing"
          aria-label={trailing.label}
          onClick={trailing.onClick}
        >
          {trailing.icon}
        </button>
      ) : null}
    </label>
  );
}
